import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { PrivacyNotice } from "./PrivacyNotice";
import {
  ArrowLeft, ArrowRight, Check, ChevronLeft, ChevronRight,
  Handshake, Send, Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  PARTNERS_HERO, PARTNERS_MICROSOFT, PARTNERS_SOLUTION,
  PARTNERS_REGIONAL, PARTNERS_UNIVERSITY, PARTNERS_INDUSTRY,
  PARTNERS_TECH, PARTNERS_WHY, PARTNERS_JOIN
} from "../data/partnersData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ id, bg = "bg-white", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${bg}`}>
    <div className="max-w-7xl mx-auto px-6">{children}</div>
  </section>
);

/* ── Partner Logo Carousel ── */
const PartnerCarousel = () => {
  const partners = PARTNERS_SOLUTION;
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const next = useCallback(() => setCurrent((p) => (p + 1) % partners.length), [partners.length]);
  const prev = () => setCurrent((p) => (p - 1 + partners.length) % partners.length);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(next, 3500);
    return () => clearInterval(timer);
  }, [isPaused, next]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Main display */}
      <div className="overflow-hidden rounded-2xl border border-[#E0D1BB]/30 bg-white">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {partners.map((p, i) => (
            <div key={i} className="w-full flex-shrink-0 flex flex-col items-center justify-center py-12 px-8">
              {p.logo ? (
                <img src={p.logo} alt={p.name} className="h-24 w-auto object-contain mb-5" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#308795]/[0.08] flex items-center justify-center mb-5">
                  <Handshake className="w-10 h-10 text-[#308795]" />
                </div>
              )}
              <h3 className="text-xl font-bold text-[#2C2A2B] mb-1">{p.name}</h3>
              <p className="text-sm text-[#6B6567]">{p.type}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 mt-6">
        <button onClick={prev} className="w-10 h-10 rounded-full border border-[#E0D1BB]/40 flex items-center justify-center text-[#6B6567] hover:border-[#308795] hover:text-[#308795] transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex gap-2">
          {partners.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === current ? "bg-[#308795] w-6" : "bg-[#D5CFC9]"}`}
            />
          ))}
        </div>
        <button onClick={next} className="w-10 h-10 rounded-full border border-[#E0D1BB]/40 flex items-center justify-center text-[#6B6567] hover:border-[#308795] hover:text-[#308795] transition-colors">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

/* ── Hero ── */
const PartnersHero = () => (
  <section className="relative min-h-[65vh] flex flex-col justify-center bg-[#465155] overflow-hidden">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#308795]/[0.06] rounded-full -translate-y-1/3 translate-x-1/3" />
    <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-16 lg:pt-40 lg:pb-24 w-full">
      <div className="flex items-center justify-between mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#E5BD77] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <div className="inline-flex items-center gap-2 bg-[#E5BD77]/15 text-[#E5BD77] text-sm font-semibold px-4 py-1.5 rounded-full">
          <span className="w-2 h-2 bg-[#E5BD77] rounded-full animate-pulse" />
          {PARTNERS_HERO.eyebrow}
        </div>
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-5 max-w-4xl">{PARTNERS_HERO.headline}</h1>
      <p className="text-lg lg:text-xl text-[#D5CFC9] leading-relaxed max-w-2xl">{PARTNERS_HERO.subheadline}</p>
    </div>
  </section>
);

/* ── Microsoft ── */
const MicrosoftSection = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
      <div>
        <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Strategic Alliance</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-2">{PARTNERS_MICROSOFT.headline}</h2>
        <p className="text-[#308795] font-semibold text-lg mb-4">{PARTNERS_MICROSOFT.subtitle}</p>
        <p className="text-[#6B6567] text-base leading-relaxed mb-6">{PARTNERS_MICROSOFT.description}</p>
        <div className="flex flex-wrap gap-2">
          {PARTNERS_MICROSOFT.qualities.map((q, i) => (
            <span key={i} className="text-xs font-medium bg-[#308795]/[0.08] text-[#308795] px-3 py-1.5 rounded-lg">{q}</span>
          ))}
        </div>
      </div>
      <div className="mt-10 lg:mt-0 space-y-3">
        {PARTNERS_MICROSOFT.achievements.map((a, i) => (
          <div key={i} className="flex items-start gap-3 bg-white border border-[#E0D1BB]/30 rounded-xl px-5 py-4">
            <Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" />
            <span className="text-sm text-[#494546]">{a}</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ── Solution Partners with Carousel ── */
const SolutionPartnersSection = () => (
  <Section bg="bg-white">
    <div className="text-center max-w-2xl mx-auto mb-14">
      <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Valued Solution Partners</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">Our Valued Solution Partners</h2>
      <p className="text-[#6B6567] text-lg">We collaborate with industry leaders to deliver comprehensive solutions.</p>
    </div>
    <div className="max-w-xl mx-auto">
      <PartnerCarousel />
    </div>
    {/* Also show all logos in a row */}
    <div className="mt-12 flex flex-wrap items-center justify-center gap-8">
      {PARTNERS_SOLUTION.map((p, i) => {
        const content = (
          <div className="flex flex-col items-center">
            {p.logo ? (
              <img src={p.logo} alt={p.name} className="h-14 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#308795]/[0.08] flex items-center justify-center">
                <Handshake className="w-7 h-7 text-[#308795]" />
              </div>
            )}
            <p className="text-[10px] text-[#8A8385] mt-2 text-center">{p.name}</p>
          </div>
        );
        return p.url ? (
          <a key={i} href={p.url} target="_blank" rel="noopener noreferrer">{content}</a>
        ) : (
          <div key={i}>{content}</div>
        );
      })}
    </div>
  </Section>
);

/* ── Regional AI ── */
const RegionalSection = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
      <div>
        <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Innovation Networks</span>
        <h2 className="text-3xl font-bold text-[#2C2A2B] leading-tight mb-2">{PARTNERS_REGIONAL.headline}</h2>
        <p className="text-[#308795] font-semibold mb-4">{PARTNERS_REGIONAL.subtitle}</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {PARTNERS_REGIONAL.locations.map((l, i) => (
            <span key={i} className="text-xs font-medium bg-[#7EA13E]/[0.08] text-[#7EA13E] px-3 py-1.5 rounded-lg">{l}</span>
          ))}
        </div>
      </div>
      <div className="mt-6 lg:mt-0 space-y-2">
        {PARTNERS_REGIONAL.capabilities.map((c, i) => (
          <div key={i} className="flex items-start gap-3 bg-white border border-[#E0D1BB]/20 rounded-xl px-5 py-3">
            <Check className="w-4 h-4 text-[#7EA13E] mt-0.5 shrink-0" />
            <span className="text-sm text-[#494546]">{c}</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ── Why + Join CTA ── */
const WhyJoinSection = () => {
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", company: "", website: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) { toast.error("Please fill in your first name, last name, and email."); return; }
    setLoading(true);
    try {
      const mp=[]; if(formData.company) mp.push(`Company: ${formData.company}`); if(formData.website) mp.push(`Website: ${formData.website}`); if(formData.message) mp.push(formData.message);
      await axios.post(`${API}/enquiries`, { name: `${formData.firstName} ${formData.lastName}`, email: formData.email, message: mp.join("\n") || null, selected_path: "Partnership Enquiry" });
      setSubmitted(true); toast.success("Thank you! We'll be in touch.");
    } catch { toast.error("Something went wrong."); } finally { setLoading(false); }
  };
  return (
    <Section bg="bg-[#465155]">
      <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-start">
        <div>
          <span className="text-sm font-bold text-[#E5BD77] uppercase tracking-widest mb-3 block">Why It Matters</span>
          <h2 className="text-3xl font-bold text-white leading-tight mb-6">Why Partnerships Matter to Our Clients</h2>
          <div className="space-y-3 mb-8">
            {PARTNERS_WHY.map((w, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/[0.06] border border-white/[0.08] rounded-xl px-5 py-3">
                <Check className="w-4 h-4 text-[#E5BD77] shrink-0" />
                <span className="text-sm text-[#D5CFC9]">{w}</span>
              </div>
            ))}
          </div>
          <h3 className="text-lg font-bold text-white mb-3">Join Our Ecosystem</h3>
          <p className="text-sm text-[#A9B294] mb-4">Whether you are:</p>
          <div className="flex flex-wrap gap-2">
            {PARTNERS_JOIN.map((j, i) => (
              <span key={i} className="text-xs font-medium bg-white/[0.06] border border-white/[0.08] text-[#D5CFC9] px-3 py-1.5 rounded-lg">{j}</span>
            ))}
          </div>
        </div>
        <div className="mt-10 lg:mt-0">
          {!submitted ? (
            <div className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Partner With Us</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-white/70 mb-1.5">First Name</label><input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] transition-all" placeholder="First name" /></div>
                  <div><label className="block text-sm font-medium text-white/70 mb-1.5">Last Name</label><input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] transition-all" placeholder="Last name" /></div>
                  <div><label className="block text-sm font-medium text-white/70 mb-1.5">Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] transition-all" placeholder="your@email.com" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-white/70 mb-1.5">Name of Company</label><input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] transition-all" placeholder="Company name" /></div>
                  <div><label className="block text-sm font-medium text-white/70 mb-1.5">Website</label><input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] transition-all" placeholder="https://www.example.com" /></div>
                </div>
                <div><label className="block text-sm font-medium text-white/70 mb-1.5">Message</label><textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={3} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] transition-all resize-none" placeholder="Tell us about partnership opportunities..." /></div>
                <PrivacyNotice checked={privacyConsent} onChange={setPrivacyConsent} dark />
                <Button type="submit" disabled={loading || !privacyConsent} className="w-full bg-[#E5BD77] hover:bg-[#d4ac66] text-[#2C2A2B] font-semibold py-6 rounded-xl transition-all duration-300 text-base disabled:opacity-60">
                  {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : (<>Submit Enquiry<Send className="ml-2 h-4 w-4" /></>)}
                </Button>
              </form>
            </div>
          ) : (
            <div className="text-center py-12"><div className="w-16 h-16 rounded-full bg-[#7EA13E]/20 flex items-center justify-center mx-auto mb-5"><svg className="w-8 h-8 text-[#7EA13E]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div><h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3><p className="text-[#D5CFC9]">We'll be in touch about partnership opportunities.</p></div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default function PartnersPage() {
  return (
    <>
      <PartnersHero />
      <MicrosoftSection />
      <SolutionPartnersSection />
      <RegionalSection />
      <WhyJoinSection />
    </>
  );
}
